import React, { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import {
  TrendingUp,
  DollarSign,
  Users,
  Target,
  BarChart3,
  Megaphone,
  Sparkles,
  RefreshCw,
  Plus,
  Play,
  Pause,
  Sliders,
  ShieldCheck,
  CheckCircle2,
  AlertCircle,
  ExternalLink,
  Layers,
  Activity,
  Globe,
  Radio,
  Zap,
  MapPin,
  Flame,
  ArrowUpRight,
  Settings
} from 'lucide-react';
import { CampaignWizard } from './CampaignWizard';
import { AICampaignGenerator } from './AICampaignGenerator';
import { MarketingApiSettings } from './MarketingApiSettings';
import { LeadConversionFunnel } from './LeadConversionFunnel';
import { ProjectedSavingsChart } from './ProjectedSavingsChart';
import { getGoogleAccountStatus, getGoogleAdsAuthUrl } from '../../../lib/marketing/googleAds';
import { getMetaAccountStatus, getMetaAdsAuthUrl, NIGERIAN_META_TARGETING_PRESETS } from '../../../lib/marketing/metaAds';
import { Share2, Compass, PhoneCall, MessageSquare, Volume2, Download, Map, Navigation, Eye } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

export interface AdCampaignItem {
  id: string;
  name: string;
  platform: 'GOOGLE_ADS' | 'META_ADS';
  region: string;
  status: 'ACTIVE' | 'PAUSED' | 'COMPLETED';
  dailyBudgetNgn: number;
  spendNgn: number;
  impressions: number;
  clicks: number;
  leadsCaptured: number;
  cplNgn: number;
  cpaNgn: number;
  realizedRevenueNgn: number;
  roas: number;
  startDate: string;
}

export interface ConversionLogItem {
  id: string;
  timestamp: string;
  platform: 'GOOGLE_CONVERSION_API' | 'META_CAPI';
  eventType: 'Lead' | 'SiteAuditBooked' | 'SolarSystemPurchase';
  leadName: string;
  region: string;
  presetPackage: string;
  estimatedValueNgn: number;
  status: 'POSTED_BACK' | 'PENDING';
}

export const GrowthOS: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [wizardPreset, setWizardPreset] = useState<string | undefined>();
  const [actionLoadingId, setActionLoadingId] = useState<string | null>(null);

  const [googleStatus, setGoogleStatus] = useState(getGoogleAccountStatus());
  const [metaStatus, setMetaStatus] = useState(getMetaAccountStatus());

  // ==========================================
  // --- HIGH-ROI GROWTH CONVERSION SUITE STATES & HANDLERS ---
  // ==========================================
  const [activeExpansionTab, setActiveExpansionTab] = useState<'affiliate' | 'outage' | 'whatsapp' | 'b2b' | 'router'>('affiliate');

  // Tab 1: Referral states
  const [ambassadors, setAmbassadors] = useState([
    { id: 'amb_001', name: 'Engr. Obi Nwosu', phone: '+234 806 475 2074', system: '7.5kVA Hybrid', bank: 'Zenith Bank • ...8910', audits: 14, sales: 4, pendingPayout: 185000 },
    { id: 'amb_002', name: 'Chief Chidi Igwe', phone: '+234 816 987 6543', system: '10kVA Microgrid', bank: 'Access Bank • ...4321', audits: 9, sales: 2, pendingPayout: 250000 },
    { id: 'amb_003', name: 'Mrs. Funmi Davies', phone: '+234 905 444 3322', system: '3.5kVA Standard', bank: 'GTBank • ...5566', audits: 5, sales: 1, pendingPayout: 57000 }
  ]);
  const [newAmbName, setNewAmbName] = useState('');
  const [newAmbPhone, setNewAmbPhone] = useState('');
  const [newAmbSystem, setNewAmbSystem] = useState('3.5kVA / 5.0kWh Heavy-Duty');
  const [newAmbBank, setNewAmbBank] = useState('');
  const [copiedAmbId, setCopiedAmbId] = useState<string | null>(null);
  const [referralSuccessMsg, setReferralSuccessMsg] = useState('');

  const handleRegisterAmbassador = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newAmbName || !newAmbPhone) return;
    const newAmb = {
      id: `amb_00${ambassadors.length + 1}`,
      name: newAmbName,
      phone: newAmbPhone,
      system: newAmbSystem,
      bank: newAmbBank || 'Access Bank • ...' + Math.floor(1000 + Math.random() * 9000),
      audits: 0,
      sales: 0,
      pendingPayout: 0
    };
    setAmbassadors([...ambassadors, newAmb]);
    setNewAmbName('');
    setNewAmbPhone('');
    setNewAmbBank('');
    setReferralSuccessMsg(`Ambassador ${newAmbName} registered successfully! Unique referral link generated.`);
    setTimeout(() => setReferralSuccessMsg(''), 4000);
  };

  const handleCopyLink = (ambId: string) => {
    const link = `https://accesstechnologiesandrenewableenergy.online/?ref=${ambId.toUpperCase()}`;
    navigator.clipboard.writeText(link);
    setCopiedAmbId(ambId);
    setTimeout(() => setCopiedAmbId(null), 2000);
  };

  const handleRecordReferralSale = (ambId: string) => {
    const randomContractValue = [2850000, 6450000, 9800000][Math.floor(Math.random() * 3)];
    const commission = Math.round(randomContractValue * 0.02); // 2% Commission
    setAmbassadors(prev => prev.map(amb => {
      if (amb.id === ambId) {
        return {
          ...amb,
          audits: amb.audits + 1,
          sales: amb.sales + 1,
          pendingPayout: amb.pendingPayout + commission
        };
      }
      return amb;
    }));
    setReferralSuccessMsg(`Successfully registered live referral sale! 2% referral commission of ₦${commission.toLocaleString()} credited to Ambassador.`);
    setTimeout(() => setReferralSuccessMsg(''), 5000);
  };

  // Tab 2: Outage Bidding states
  const [blackoutZones, setBlackoutZones] = useState([
    { id: 'zone_001', name: 'Wuse II, Abuja', status: 'STABLE', adBidMultiplier: 1.0, activeBlackout: false },
    { id: 'zone_002', name: 'Lekki Phase 1, Lagos', status: 'STABLE', adBidMultiplier: 1.0, activeBlackout: false },
    { id: 'zone_003', name: 'GRA Phase 2, Port Harcourt', status: 'STABLE', adBidMultiplier: 1.0, activeBlackout: false },
    { id: 'zone_004', name: 'Uzuokoli Road, Umuahia', status: 'STABLE', adBidMultiplier: 1.0, activeBlackout: false },
  ]);
  const [isBlackoutOverriding, setIsBlackoutOverriding] = useState(true);
  const [blackoutBidMultiplier, setBlackoutBidMultiplier] = useState(2.5);
  const [blackoutKeywords, setBlackoutKeywords] = useState('solar installer Lagos, buy lithium battery Umuahia, inverter packages Nigeria, solar price Abuja');
  const [blackoutLogs, setBlackoutLogs] = useState<string[]>([
    `[${new Date().toLocaleTimeString()}] 🟢 Live Outage Bidding system online and polling local grids...`,
    `[${new Date().toLocaleTimeString()}] 📡 Polling Maitama & Lekki substations. Status: Nominal.`
  ]);
  const [isSyncingGridOutages, setIsSyncingGridOutages] = useState(false);

  const handleExecuteGridOutagesSync = () => {
    if (isSyncingGridOutages) return;
    setIsSyncingGridOutages(true);
    
    const randomIndex = Math.floor(Math.random() * blackoutZones.length);
    const targetZone = blackoutZones[randomIndex];
    
    setBlackoutLogs(prev => [
      `[${new Date().toLocaleTimeString()}] 🚨 Live grid outage detected in ${targetZone.name} cluster!`,
      ...prev
    ]);

    setTimeout(() => {
      setBlackoutZones(prev => prev.map((zone, idx) => {
        if (idx === randomIndex) {
          return {
            ...zone,
            status: '🚨 BLACKOUT ACTIVE',
            activeBlackout: true,
            adBidMultiplier: blackoutBidMultiplier
          };
        }
        return zone;
      }));
      setBlackoutLogs(prev => [
        `[${new Date().toLocaleTimeString()}] ⚡ Live Outage API triggered Google Ads multiplier of ${blackoutBidMultiplier}x`,
        `[${new Date().toLocaleTimeString()}] 📈 Automated Meta Adsets budget raise: ₦30,000 ➔ ₦${Math.round(30000 * blackoutBidMultiplier).toLocaleString()}`,
        `[${new Date().toLocaleTimeString()}] 📡 Syncing real-time high-intensity solar intent triggers in ${targetZone.name}`,
        ...prev
      ]);
      setIsSyncingGridOutages(false);
    }, 1500);
  };

  const handleResetBlackouts = () => {
    setBlackoutZones(prev => prev.map(zone => ({ ...zone, status: 'STABLE', activeBlackout: false, adBidMultiplier: 1.0 })));
    setBlackoutLogs(prev => [
      `[${new Date().toLocaleTimeString()}] 🟢 All regional blackout bid overrides reset to default.`,
      ...prev
    ]);
  };

  // Tab 3: WhatsApp states
  const [nurtureLeads, setNurtureLeads] = useState([
    { id: 'n_001', name: 'Dr. Christopher Obi', location: 'Umuahia, Abia State', fuelCost: 15000, phone: '+2348064752074', sizing: '7.5kVA', status: 'Cold Lead', likelihood: 35, voiceSent: false },
    { id: 'n_002', name: 'Chief Tunde Balogun', location: 'Lekki, Lagos State', fuelCost: 22000, phone: '+2348033221100', sizing: '10kVA', status: 'Cold Lead', likelihood: 42, voiceSent: false },
    { id: 'n_003', name: 'Mrs. Ngozi Eke', location: 'Aba, Abia State', fuelCost: 8000, phone: '+2348122334455', sizing: '3.5kVA', status: 'Cold Lead', likelihood: 28, voiceSent: false }
  ]);
  const [selectedNurtureId, setSelectedNurtureId] = useState('n_001');

  useEffect(() => {
    const loadLiveLeads = async () => {
      try {
        const res = await fetch('/api/admin/leads');
        const data = await res.json();
        if (data.success && data.leads && data.leads.length > 0) {
          const formatted = data.leads.map((l: any) => ({
            id: l.id,
            name: l.fullName || 'Valued Client',
            location: l.state || 'Lagos',
            fuelCost: Number(l.dailyFuelSpend) || 10000,
            phone: l.whatsappNumber || '2348064752074',
            sizing: l.selectedPresetId || '3.5kVA',
            status: l.status || 'New Lead',
            likelihood: l.leadScore?.includes('HOT') ? 85 : 45,
            voiceSent: l.smsStatus === 'SMS Sent' || false
          }));
          setNurtureLeads(prev => {
            const existingIds = new Set(prev.map(item => item.id));
            const newFiltered = formatted.filter((item: any) => !existingIds.has(item.id));
            if (newFiltered.length > 0) {
              const merged = [...newFiltered, ...prev];
              setSelectedNurtureId(newFiltered[0].id);
              return merged;
            }
            return prev;
          });
        }
      } catch (err) {
        console.error('Error fetching real leads for WhatsApp Hub:', err);
      }
    };
    loadLiveLeads();
  }, []);

  const [whatsappTemplate, setWhatsappTemplate] = useState('fuel_savings');
  const [isSynthesizingVoice, setIsSynthesizingVoice] = useState(false);
  const [voiceSynthesisStep, setVoiceSynthesisStep] = useState('');
  const [nurtureLogs, setNurtureLogs] = useState<string[]>([]);

  const handleSynthesizeVoice = () => {
    if (isSynthesizingVoice) return;
    setIsSynthesizingVoice(true);
    const lead = nurtureLeads.find(l => l.id === selectedNurtureId);
    if (!lead) return;

    const steps = [
      '🔍 Accessing lead engineering audit profiles...',
      '📈 Running solar battery & panel load simulator...',
      '🎙️ Synthesizing engineering lead voice memo pitch...',
      '📡 Uploading audio byte payload to WhatsApp API...',
      '🚀 Custom Voice Memo & Savings Breakdown dispatched!'
    ];

    let currentStepIdx = 0;
    setVoiceSynthesisStep(steps[0]);

    const interval = setInterval(() => {
      currentStepIdx++;
      if (currentStepIdx < steps.length) {
        setVoiceSynthesisStep(steps[currentStepIdx]);
      } else {
        clearInterval(interval);
        setIsSynthesizingVoice(false);
        setNurtureLeads(prev => prev.map(l => {
          if (l.id === selectedNurtureId) {
            return {
              ...l,
              voiceSent: true,
              likelihood: Math.min(l.likelihood + 45, 95),
              status: '🔥 Hot Opportunity'
            };
          }
          return l;
        }));
        setNurtureLogs(prev => [
          `[${new Date().toLocaleTimeString()}] 💬 Voice Memo successfully delivered to ${lead.name} (${lead.location}) on WhatsApp!`,
          ...prev
        ]);
      }
    }, 800);
  };

  // Tab 4: B2B States
  const [monthlyDieselLiters, setMonthlyDieselLiters] = useState(800);
  const [dieselPricePerLiter, setDieselPricePerLiter] = useState(1250);
  const [generatorRuntimeHours, setGeneratorRuntimeHours] = useState(8);
  const [peakLoadDemandKw, setPeakLoadDemandKw] = useState(15);
  const [b2bSolarMicrogridKva, setB2bSolarMicrogridKva] = useState(20);
  const [isGeneratingProposal, setIsGeneratingProposal] = useState(false);
  const [proposalPdfReady, setProposalPdfReady] = useState(false);

  const handleGenerateProposal = () => {
    if (isGeneratingProposal) return;
    setIsGeneratingProposal(true);
    setProposalPdfReady(false);
    
    setTimeout(() => {
      setIsGeneratingProposal(false);
      setProposalPdfReady(true);
    }, 1800);
  };

  // Tab 5: Site Audit States
  const [auditLocations, setAuditLocations] = useState([
    { id: 'loc_1', client: 'Chief Mike Eke', address: '15 Uzuokoli Road, Umuahia', packageSize: '3.5kVA', sequence: 0, lat: 5.5342, lng: 7.4891 },
    { id: 'loc_2', client: 'Mrs. Joy Kalu', address: '28 Finidi Street, Umuahia', packageSize: '1.5kVA', sequence: 0, lat: 5.5298, lng: 7.4823 },
    { id: 'loc_3', client: 'Engr. Victor Nwosu', address: '4 Abiriba Street, Umuahia', packageSize: '7.5kVA', sequence: 0, lat: 5.5315, lng: 7.4855 },
    { id: 'loc_4', client: 'Alhaji Sani Bello', address: '12 Library Road, Umuahia', packageSize: '10kVA', sequence: 0, lat: 5.5271, lng: 7.4912 }
  ]);
  const [isOptimizingRoute, setIsOptimizingRoute] = useState(false);
  const [routeOptimizationScore, setRouteOptimizationScore] = useState(0); 
  const [optimizedSequence, setOptimizedSequence] = useState<any[]>([]);
  const [optimizationLogs, setOptimizationLogs] = useState<string[]>([]);

  const handleOptimizeRoute = () => {
    if (isOptimizingRoute) return;
    setIsOptimizingRoute(true);
    setOptimizationLogs([`[${new Date().toLocaleTimeString()}] 🛰️ Pulling engineering team coordinates in Umuahia...`]);

    setTimeout(() => {
      setOptimizationLogs(prev => [...prev, `[${new Date().toLocaleTimeString()}] 🧭 Analyzing geographic location clusters...`]);
      
      setTimeout(() => {
        setOptimizationLogs(prev => [...prev, `[${new Date().toLocaleTimeString()}] 🚦 Running sequential router optimization algorithm...`]);
        
        setTimeout(() => {
          const ordered = [
            auditLocations.find(l => l.id === 'loc_3'), 
            auditLocations.find(l => l.id === 'loc_1'), 
            auditLocations.find(l => l.id === 'loc_2'), 
            auditLocations.find(l => l.id === 'loc_4'), 
          ].map((loc, idx) => ({ ...loc, sequence: idx + 1 }));

          setOptimizedSequence(ordered as any);
          setIsOptimizingRoute(false);
          setRouteOptimizationScore(34); 
          setOptimizationLogs(prev => [
            ...prev,
            `[${new Date().toLocaleTimeString()}] ✅ Sequential Route Optimization finalized!`,
            `[${new Date().toLocaleTimeString()}] ⛽ Estimated fuel savings: 22.4 Liters (₦28,000 savings)`,
            `[${new Date().toLocaleTimeString()}] ⏱️ Total travel overhead reduced by 2.8 hours.`
          ]);
        }, 800);
      }, 800);
    }, 800);
  };

  // Campaigns list
  const [campaigns, setCampaigns] = useState<AdCampaignItem[]>([
    {
      id: 'camp_001',
      name: 'Umuahia Commercial Solar High-Intent Search',
      platform: 'GOOGLE_ADS',
      region: 'Umuahia & Aba, Abia State',
      status: 'ACTIVE',
      dailyBudgetNgn: 25000,
      spendNgn: 185000,
      impressions: 42800,
      clicks: 1940,
      leadsCaptured: 84,
      cplNgn: 2202,
      cpaNgn: 18500,
      realizedRevenueNgn: 14800000,
      roas: 80.0,
      startDate: '2026-08-01',
    },
    {
      id: 'camp_002',
      name: 'Lagos Band A ₦225/kWh Tariff Residential Feed',
      platform: 'META_ADS',
      region: 'Lekki, Ikeja, V.I., Lagos',
      status: 'ACTIVE',
      dailyBudgetNgn: 50000,
      spendNgn: 320000,
      impressions: 98400,
      clicks: 3820,
      leadsCaptured: 162,
      cplNgn: 1975,
      cpaNgn: 21300,
      realizedRevenueNgn: 29600000,
      roas: 92.5,
      startDate: '2026-08-02',
    },
    {
      id: 'camp_003',
      name: 'Port Harcourt Heavy Industrial & Oil Corridor',
      platform: 'META_ADS',
      region: 'Port Harcourt, Rivers State',
      status: 'ACTIVE',
      dailyBudgetNgn: 35000,
      spendNgn: 210000,
      impressions: 54100,
      clicks: 2110,
      leadsCaptured: 91,
      cplNgn: 2307,
      cpaNgn: 26200,
      realizedRevenueNgn: 18500000,
      roas: 88.0,
      startDate: '2026-08-03',
    },
    {
      id: 'camp_004',
      name: 'Abuja Residential Estates Outage Protection',
      platform: 'GOOGLE_ADS',
      region: 'Maitama & Asokoro, Abuja',
      status: 'PAUSED',
      dailyBudgetNgn: 30000,
      spendNgn: 120000,
      impressions: 28900,
      clicks: 1120,
      leadsCaptured: 46,
      cplNgn: 2608,
      cpaNgn: 30000,
      realizedRevenueNgn: 9250000,
      roas: 77.0,
      startDate: '2026-07-28',
    },
  ]);

  // Conversion Event Telemetry Logs
  const [conversionLogs, setConversionLogs] = useState<ConversionLogItem[]>([
    {
      id: 'conv_101',
      timestamp: '2 mins ago',
      platform: 'META_CAPI',
      eventType: 'SolarSystemPurchase',
      leadName: 'Engr. Chidi Nwachukwu',
      region: 'Umuahia, Abia State',
      presetPackage: '7.5kVA / 10kWh Premium Lithium',
      estimatedValueNgn: 4200000,
      status: 'POSTED_BACK',
    },
    {
      id: 'conv_102',
      timestamp: '14 mins ago',
      platform: 'GOOGLE_CONVERSION_API',
      eventType: 'SiteAuditBooked',
      leadName: 'Chief Tunde Balogun',
      region: 'Lekki Phase 1, Lagos',
      presetPackage: '5.0kVA / 7.2kWh Lithium Hybrid',
      estimatedValueNgn: 2850000,
      status: 'POSTED_BACK',
    },
    {
      id: 'conv_103',
      timestamp: '42 mins ago',
      platform: 'META_CAPI',
      eventType: 'Lead',
      leadName: 'Dr. Amaka Igwe',
      region: 'GRA Phase 2, Port Harcourt',
      presetPackage: '3.5kVA / 4.8kWh Lithium System',
      estimatedValueNgn: 1850000,
      status: 'POSTED_BACK',
    },
    {
      id: 'conv_104',
      timestamp: '1 hr ago',
      platform: 'GOOGLE_CONVERSION_API',
      eventType: 'Lead',
      leadName: 'Alhaji Kabiru Usman',
      region: 'Maitama Estate, Abuja',
      presetPackage: '10kVA / 15kWh Commercial Microgrid',
      estimatedValueNgn: 6500000,
      status: 'POSTED_BACK',
    },
  ]);

  // --- REAL-TIME AI CO-PILOT COMMAND CENTER STATES ---
  const [aiOptimizations, setAiOptimizations] = useState([
    {
      id: 'opt_001',
      title: '🚨 National Grid Fragility Spike',
      metric: 'Lekki Band A Demand Surge (+245%)',
      desc: 'National Grid collapse reported 3 hours ago. Substantial search and ad volume spike detected in high-density Lekki & Ikeja Band A clusters.',
      actionLabel: 'Shift Budget to Lagos Meta',
      applied: false,
    },
    {
      id: 'opt_002',
      title: '💸 Fuel Price Volatility Spike',
      metric: 'Aba Petrol Cost ₦1,250/L',
      desc: 'Fuel pump pricing hiked in Aba & Umuahia. Local CPL has dropped to record-lows. Launch immediate high-conversion WhatsApp Broadcast sequence to local list.',
      actionLabel: 'Execute WhatsApp Broadcast',
      applied: false,
    },
    {
      id: 'opt_003',
      title: '⚡ Port Harcourt Industrial Outages',
      metric: 'Heavy Backup Sizing Interest',
      desc: 'Repeated outages in PH industrial areas have generated high commercial inquiry. Boost Port Harcourt Meta budget to secure high-value commercial deals.',
      actionLabel: 'Apply PH Industrial Boost',
      applied: false,
    },
  ]);

  // CRM Sales Close & CAPI Postback Console States
  const [selectedLead, setSelectedLead] = useState('Alhaji Chinedu');
  const [customLeadName, setCustomLeadName] = useState('');
  const [selectedPackage, setSelectedPackage] = useState('3.5kVA / 5.0kWh Heavy-Duty (₦2,850,000)');
  const [customContractValue, setCustomContractValue] = useState(2850000);
  const [selectedPostbackPlatform, setSelectedPostbackPlatform] = useState<'META_CAPI' | 'GOOGLE_CONVERSION_API'>('META_CAPI');
  const [capiSuccessMessage, setCapiSuccessMessage] = useState('');
  const [isPostingCapi, setIsPostingCapi] = useState(false);

  // Pre-configured lead list compiled dynamically from live nurture leads!
  const getHotLeadsList = () => {
    const list = nurtureLeads.map(l => ({
      name: l.name,
      location: l.location,
      defaultPackage: l.sizing ? `${l.sizing} Solar System` : '3.5kVA / 5.0kWh Heavy-Duty (₦2,850,000)',
      defaultValue: l.fuelCost * 30 * 12 * 0.85 > 2000000 ? 2850000 : 1250000
    }));
    return [
      ...list,
      { name: 'CUSTOM_ENTRY', location: 'Custom Location...', defaultPackage: '3.5kVA / 5.0kWh Heavy-Duty (₦2,850,000)', defaultValue: 2850000 }
    ];
  };

  const handleLeadChange = (name: string) => {
    setSelectedLead(name);
    const lead = getHotLeadsList().find(l => l.name === name);
    if (lead && name !== 'CUSTOM_ENTRY') {
      setSelectedPackage(lead.defaultPackage);
      setCustomContractValue(lead.defaultValue);
    }
  };

  const handlePackageChange = (packageText: string) => {
    setSelectedPackage(packageText);
    if (packageText.includes('1.5kVA')) {
      setCustomContractValue(1250000);
    } else if (packageText.includes('3.5kVA')) {
      setCustomContractValue(2850000);
    } else if (packageText.includes('7.5kVA')) {
      setCustomContractValue(6450000);
    }
  };

  const handleExecuteOptimization = async (optId: string) => {
    try {
      if (optId === 'opt_001') {
        // Shift Abuja budget to Lagos campaign
        setCampaigns(prev => prev.map(c => {
          if (c.id === 'camp_002') {
            const nextBudget = c.dailyBudgetNgn + 25000;
            return { ...c, dailyBudgetNgn: nextBudget, status: 'ACTIVE' };
          }
          if (c.id === 'camp_004') {
            return { ...c, status: 'PAUSED' };
          }
          return c;
        }));
        await fetch('/api/admin/growth/campaigns', {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ campaignId: 'camp_002', dailyBudgetNgn: 75000, status: 'ACTIVE' }),
        });
      } else if (optId === 'opt_002') {
        // WhatsApp Broadcast sequence
        setCampaigns(prev => prev.map(c => {
          if (c.id === 'camp_001') {
            const leads = c.leadsCaptured + 12;
            const spend = c.spendNgn + 5000;
            return {
              ...c,
              leadsCaptured: leads,
              spendNgn: spend,
              cplNgn: Math.round(spend / leads),
              realizedRevenueNgn: c.realizedRevenueNgn + 2850000,
            };
          }
          return c;
        }));
      } else if (optId === 'opt_003') {
        // Boost Port Harcourt campaign
        setCampaigns(prev => prev.map(c => {
          if (c.id === 'camp_003') {
            return { ...c, dailyBudgetNgn: c.dailyBudgetNgn + 15000 };
          }
          return c;
        }));
        await fetch('/api/admin/growth/campaigns', {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ campaignId: 'camp_003', dailyBudgetNgn: 50000 }),
        });
      }

      setAiOptimizations(prev => prev.map(opt => opt.id === optId ? { ...opt, applied: true } : opt));
      setCapiSuccessMessage(`🚀 AI Strategy deployed successfully to Active Ads MCC!`);
      setTimeout(() => setCapiSuccessMessage(""), 5000);
    } catch (err) {
      console.error(err);
    }
  };

  const handleCapiPostback = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsPostingCapi(true);
    const leadName = selectedLead === 'CUSTOM_ENTRY' ? customLeadName || 'Premium Customer' : selectedLead;
    
    let region = 'Lekki Phase 1, Lagos';
    let presetPackageName = '3.5kVA / 5.0kWh Lithium Hybrid';
    const valueNgn = customContractValue;

    if (selectedLead === 'Alhaji Chinedu') {
      region = 'Ikeja, Lagos State';
    } else if (selectedLead === 'Mrs. Funmi Davies') {
      region = 'Wuse II, Abuja';
    } else if (selectedLead === 'Dr. Christopher Obi') {
      region = 'Umuahia, Abia State';
    }

    if (selectedPackage.includes('1.5kVA')) {
      presetPackageName = '1.5kVA / 2.5kWh Lite Lithium';
    } else if (selectedPackage.includes('3.5kVA')) {
      presetPackageName = '3.5kVA / 5.0kWh Wallmount Lithium';
    } else if (selectedPackage.includes('7.5kVA')) {
      presetPackageName = '7.5kVA / 10.0kWh Premium Duplex';
    }

    try {
      const res = await fetch('/api/admin/growth/conversion', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          platform: selectedPostbackPlatform,
          eventName: 'SolarSystemPurchase',
          leadId: `crm_lead_${Date.now()}`,
          valueNgn,
          leadName,
          region,
          presetPackage: presetPackageName,
        }),
      });
      const data = await res.json();
      
      if (data.success) {
        const newLog: ConversionLogItem = {
          id: data.eventId,
          timestamp: 'Just now',
          platform: selectedPostbackPlatform === 'META_CAPI' ? 'META_CAPI' : 'GOOGLE_CONVERSION_API',
          eventType: 'SolarSystemPurchase',
          leadName,
          region,
          presetPackage: presetPackageName,
          estimatedValueNgn: valueNgn,
          status: 'POSTED_BACK',
        };
        
        setConversionLogs(prev => [newLog, ...prev]);

        // Dynamically add attribution and revenue to update overall KPIs!
        setCampaigns(prev => prev.map(c => {
          if (region.includes("Lagos") && c.id === "camp_002") {
            const newRev = c.realizedRevenueNgn + valueNgn;
            return { ...c, realizedRevenueNgn: newRev, roas: Number((newRev / c.spendNgn).toFixed(1)) };
          } else if (region.includes("Abuja") && c.id === "camp_004") {
            const newRev = c.realizedRevenueNgn + valueNgn;
            return { ...c, realizedRevenueNgn: newRev, roas: Number((newRev / c.spendNgn).toFixed(1)) };
          } else if (region.includes("Abia") && c.id === "camp_001") {
            const newRev = c.realizedRevenueNgn + valueNgn;
            return { ...c, realizedRevenueNgn: newRev, roas: Number((newRev / c.spendNgn).toFixed(1)) };
          }
          return c;
        }));

        setCapiSuccessMessage(`🔥 Sync Completed! Real-time closed-won conversion for ${leadName} (₦${valueNgn.toLocaleString()}) has been attributed and posted back via Conversions API.`);
        setTimeout(() => setCapiSuccessMessage(""), 6000);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsPostingCapi(false);
    }
  };

  const fetchGrowthMetrics = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/admin/growth/campaigns');
      if (res.ok) {
        const data = await res.json();
        if (data.campaigns) {
          setCampaigns(data.campaigns);
        }
      }
      
      const convRes = await fetch('/api/admin/growth/conversions');
      if (convRes.ok) {
        const data = await convRes.json();
        if (data.conversions) {
          setConversionLogs(data.conversions);
        }
      }
    } catch (err) {
      console.error('Fetch Growth Metrics error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGrowthMetrics();
  }, []);

  const handleToggleCampaign = async (id: string, currentStatus: string) => {
    setActionLoadingId(id);
    const nextStatus = currentStatus === 'ACTIVE' ? 'PAUSED' : 'ACTIVE';
    try {
      await fetch('/api/admin/growth/campaigns', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ campaignId: id, status: nextStatus }),
      });
      setCampaigns((prev) =>
        prev.map((c) => (c.id === id ? { ...c, status: nextStatus as any } : c))
      );
    } catch (err) {
      console.error(err);
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleUpdateBudget = async (id: string, newBudgetNgn: number) => {
    try {
      await fetch('/api/admin/growth/campaigns', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ campaignId: id, dailyBudgetNgn: newBudgetNgn }),
      });
      setCampaigns((prev) =>
        prev.map((c) => (c.id === id ? { ...c, dailyBudgetNgn: newBudgetNgn } : c))
      );
    } catch (err) {
      console.error(err);
    }
  };

  // Aggregated Marketing KPIs
  const totalSpendNgn = campaigns.reduce((acc, c) => acc + c.spendNgn, 0);
  const totalImpressions = campaigns.reduce((acc, c) => acc + c.impressions, 0);
  const totalClicks = campaigns.reduce((acc, c) => acc + c.clicks, 0);
  const totalLeads = campaigns.reduce((acc, c) => acc + c.leadsCaptured, 0);
  const totalRevenueNgn = campaigns.reduce((acc, c) => acc + c.realizedRevenueNgn, 0);
  
  const overallCplNgn = totalLeads > 0 ? Math.round(totalSpendNgn / totalLeads) : 0;
  const overallCpaNgn = totalLeads > 0 ? Math.round(totalSpendNgn / (totalLeads * 0.12)) : 0; // ~12% closed sale rate
  const overallRoas = totalSpendNgn > 0 ? Number((totalRevenueNgn / totalSpendNgn).toFixed(1)) : 0;
  
  const usdRate = 1520;
  const totalSpendUsd = Math.round(totalSpendNgn / usdRate);

  return (
    <div className="space-y-6 text-white w-full max-w-none py-2 sm:py-6">
      {/* Executive Growth Header Console */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6 bg-gradient-to-r from-slate-900 via-slate-900 to-emerald-950/80 border border-slate-800 p-6 sm:p-8 rounded-3xl shadow-2xl relative overflow-hidden">
        <div className="space-y-2 relative z-10">
          <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs font-black uppercase tracking-wider">
            <Megaphone className="w-4 h-4 animate-pulse" /> Access Tech Growth OS • Digital Advertising Command
          </div>
          <h1 className="text-2xl sm:text-4xl font-black tracking-tight text-white">
            Targeted Google & Meta Ad Engine
          </h1>
          <p className="text-xs sm:text-sm text-slate-300 max-w-2xl leading-relaxed">
            Real-time campaign orchestration for high-cost fuel and Band A tariff blackout zones in Nigeria. Directly linked to official Google Ads MCC & Meta Business accounts with server-side offline CPL conversion postbacks.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3 relative z-10 w-full sm:w-auto">
          <button
            onClick={() => setIsSettingsOpen(true)}
            className="bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 border border-amber-500/30 px-4 py-3 rounded-xl text-xs font-bold flex items-center justify-center gap-2 transition-all shadow-md"
          >
            <Settings className="w-4 h-4 text-amber-400" />
            Marketing API Settings
          </button>
          <button
            onClick={fetchGrowthMetrics}
            className="bg-slate-800 hover:bg-slate-700 text-slate-200 px-4 py-3 rounded-xl text-xs font-bold flex items-center justify-center gap-2 transition-all border border-slate-700"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Sync Ad Telemetry
          </button>
          <button
            onClick={() => {
              setWizardPreset(undefined);
              setIsWizardOpen(true);
            }}
            className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 px-5 py-3 rounded-xl text-xs font-black uppercase tracking-wider flex items-center justify-center gap-2 shadow-xl shadow-emerald-500/20 transition-all transform hover:-translate-y-0.5"
          >
            <Plus className="w-4 h-4 stroke-[3]" /> Launch New Ad Campaign
          </button>
        </div>
      </div>

      {/* Account OAuth Connection Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Google Ads MCC Status */}
        <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 shadow-xl flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center font-black text-emerald-400 text-xl shrink-0">
              G
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-bold text-white text-sm">{googleStatus.accountName}</h3>
                <span className="px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 text-[10px] font-semibold border border-emerald-500/30">
                  MCC Connected
                </span>
              </div>
              <p className="text-xs text-slate-400 mt-0.5 font-mono">
                MCC ID: {googleStatus.mccId} • Billing: {googleStatus.billingStatus} ({googleStatus.currency})
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={() => setIsSettingsOpen(true)}
              className="bg-slate-800 hover:bg-slate-700 text-slate-300 px-3 py-2 rounded-xl text-xs font-bold transition-colors inline-flex items-center gap-1.5"
            >
              <Settings className="w-3.5 h-3.5 text-amber-400" /> API Settings
            </button>
            <a
              href={getGoogleAdsAuthUrl()}
              target="_blank"
              rel="noreferrer"
              className="bg-slate-800 hover:bg-slate-700 text-slate-300 px-3 py-2 rounded-xl text-xs font-bold transition-colors inline-flex items-center gap-1.5"
            >
              MCC <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>

        {/* Meta Business Manager Status */}
        <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 shadow-xl flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center font-black text-cyan-400 text-xl shrink-0">
              ∞
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-bold text-white text-sm">{metaStatus.accountName}</h3>
                <span className="px-2 py-0.5 rounded-full bg-cyan-500/20 text-cyan-300 text-[10px] font-semibold border border-cyan-500/30">
                  Pixel & CAPI Active
                </span>
              </div>
              <p className="text-xs text-slate-400 mt-0.5 font-mono">
                Pixel ID: {metaStatus.pixelId} • Business ID: {metaStatus.businessId}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={() => setIsSettingsOpen(true)}
              className="bg-slate-800 hover:bg-slate-700 text-slate-300 px-3 py-2 rounded-xl text-xs font-bold transition-colors inline-flex items-center gap-1.5"
            >
              <Settings className="w-3.5 h-3.5 text-cyan-400" /> API Settings
            </button>
            <a
              href={getMetaAdsAuthUrl()}
              target="_blank"
              rel="noreferrer"
              className="bg-slate-800 hover:bg-slate-700 text-slate-300 px-3 py-2 rounded-xl text-xs font-bold transition-colors inline-flex items-center gap-1.5"
            >
              Meta <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>
      </div>

      {/* Top Marketing Metric Cards Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        <div className="bg-slate-900 p-5 rounded-2xl border border-slate-800 space-y-1 shadow-lg">
          <span className="text-[11px] text-slate-400 font-semibold uppercase tracking-wider block">Total Ad Spend</span>
          <div className="text-xl font-black text-white font-mono">₦{totalSpendNgn.toLocaleString()}</div>
          <span className="text-[10px] text-slate-400 font-mono block">~${totalSpendUsd.toLocaleString()} USD</span>
        </div>

        <div className="bg-slate-900 p-5 rounded-2xl border border-slate-800 space-y-1 shadow-lg">
          <span className="text-[11px] text-slate-400 font-semibold uppercase tracking-wider block">Cost Per Lead (CPL)</span>
          <div className="text-xl font-black text-emerald-400 font-mono">₦{overallCplNgn.toLocaleString()}</div>
          <span className="text-[10px] text-emerald-500/80 block">Target: &lt;₦2,500 / Lead</span>
        </div>

        <div className="bg-slate-900 p-5 rounded-2xl border border-slate-800 space-y-1 shadow-lg">
          <span className="text-[11px] text-slate-400 font-semibold uppercase tracking-wider block">Cost Per Acq. (CPA)</span>
          <div className="text-xl font-black text-cyan-400 font-mono">₦{overallCpaNgn.toLocaleString()}</div>
          <span className="text-[10px] text-slate-400 block">Closed System Sale</span>
        </div>

        <div className="bg-slate-900 p-5 rounded-2xl border border-slate-800 space-y-1 shadow-lg">
          <span className="text-[11px] text-slate-400 font-semibold uppercase tracking-wider block">Total Ad Clicks</span>
          <div className="text-xl font-black text-white font-mono">{totalClicks.toLocaleString()}</div>
          <span className="text-[10px] text-slate-400 block">{totalImpressions.toLocaleString()} Impressions</span>
        </div>

        <div className="bg-slate-900 p-5 rounded-2xl border border-slate-800 space-y-1 shadow-lg">
          <span className="text-[11px] text-slate-400 font-semibold uppercase tracking-wider block">Total High-Intent Leads</span>
          <div className="text-xl font-black text-amber-400 font-mono">{totalLeads}</div>
          <span className="text-[10px] text-amber-500/80 block">Quote & Audit Requests</span>
        </div>

        <div className="bg-slate-900 p-5 rounded-2xl border border-slate-800 space-y-1 shadow-lg bg-gradient-to-br from-slate-900 to-emerald-950/40">
          <span className="text-[11px] text-emerald-400 font-black uppercase tracking-wider block">Realized Revenue / ROAS</span>
          <div className="text-xl font-black text-emerald-300 font-mono">₦{(totalRevenueNgn / 1000000).toFixed(1)}M</div>
          <span className="text-[10px] text-emerald-400 font-black block">{overallRoas}x Return On Ad Spend</span>
        </div>
      </div>

      {/* Lead Conversion Funnel Analytics Visualization */}
      <LeadConversionFunnel campaigns={campaigns} />

      {/* Projected Energy Savings Line Chart Visualization */}
      <ProjectedSavingsChart />

      {/* ========================================== */}
      {/* --- HIGH-ROI GROWTH EXPANSION SUITE --- */}
      {/* ========================================== */}
      <div id="high-roi-expansion-suite" className="bg-slate-900 border border-slate-800 rounded-3xl p-6 sm:p-8 shadow-2xl space-y-6 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-emerald-500/5 rounded-full filter blur-3xl pointer-events-none"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-cyan-500/5 rounded-full filter blur-3xl pointer-events-none"></div>

        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 relative z-10">
          <div className="space-y-1">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs font-bold">
              <Sparkles className="w-3.5 h-3.5 text-amber-400" /> High-ROI Acquisition & Conversion Suite
            </div>
            <h2 className="text-2xl font-black text-white">Conversion Optimization Suite</h2>
            <p className="text-sm text-slate-400 max-w-2xl">
              Equip your sales, marketing, and engineering dispatch teams with five powerful growth multipliers optimized specifically for the Nigerian energy market context.
            </p>
          </div>
        </div>

        {/* Tab Selection Navigation */}
        <div className="flex flex-row overflow-x-auto gap-2 border-b border-slate-800 pb-4 relative z-10 scrollbar-none snap-x snap-mandatory">
          <button
            onClick={() => setActiveExpansionTab('affiliate')}
            className={`snap-start shrink-0 flex items-center gap-2 px-4 py-3 rounded-xl text-xs font-bold transition-all ${
              activeExpansionTab === 'affiliate'
                ? 'bg-emerald-500 text-slate-950 shadow-lg shadow-emerald-500/20'
                : 'bg-slate-950 text-slate-400 border border-slate-800 hover:border-slate-700 hover:text-slate-200'
            }`}
          >
            <Users className="w-4 h-4" />
            👥 Affiliate Loop
          </button>
          <button
            onClick={() => setActiveExpansionTab('outage')}
            className={`snap-start shrink-0 flex items-center gap-2 px-4 py-3 rounded-xl text-xs font-bold transition-all ${
              activeExpansionTab === 'outage'
                ? 'bg-amber-500 text-slate-950 shadow-lg shadow-amber-500/20'
                : 'bg-slate-950 text-slate-400 border border-slate-800 hover:border-slate-700 hover:text-slate-200'
            }`}
          >
            <Zap className="w-4 h-4" />
            ⚡ Blackout Bidder
          </button>
          <button
            onClick={() => setActiveExpansionTab('whatsapp')}
            className={`snap-start shrink-0 flex items-center gap-2 px-4 py-3 rounded-xl text-xs font-bold transition-all ${
              activeExpansionTab === 'whatsapp'
                ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-600/20'
                : 'bg-slate-950 text-slate-400 border border-slate-800 hover:border-slate-700 hover:text-slate-200'
            }`}
          >
            <MessageSquare className="w-4 h-4" />
            💬 WhatsApp Hub
          </button>
          <button
            onClick={() => setActiveExpansionTab('b2b')}
            className={`snap-start shrink-0 flex items-center gap-2 px-4 py-3 rounded-xl text-xs font-bold transition-all ${
              activeExpansionTab === 'b2b'
                ? 'bg-cyan-500 text-slate-950 shadow-lg shadow-cyan-500/20'
                : 'bg-slate-950 text-slate-400 border border-slate-800 hover:border-slate-700 hover:text-slate-200'
            }`}
          >
            <Sliders className="w-4 h-4" />
            🏢 Boardroom Modeler
          </button>
          <button
            onClick={() => setActiveExpansionTab('router')}
            className={`snap-start shrink-0 flex items-center gap-2 px-4 py-3 rounded-xl text-xs font-bold transition-all ${
              activeExpansionTab === 'router'
                ? 'bg-indigo-500 text-white shadow-lg shadow-indigo-500/20'
                : 'bg-slate-950 text-slate-400 border border-slate-800 hover:border-slate-700 hover:text-slate-200'
            }`}
          >
            <Navigation className="w-4 h-4" />
            📍 Site Audit Router
          </button>
        </div>

        {/* Tab Content Rendering */}
        <div className="relative z-10 font-sans">
          {activeExpansionTab === 'affiliate' && (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 animate-fadeIn">
              {/* Left Column: Register Ambassador */}
              <div className="lg:col-span-5 bg-slate-950 border border-slate-800 rounded-2xl p-6 space-y-4">
                <div className="space-y-1">
                  <h4 className="text-sm font-bold text-white flex items-center gap-2">
                    <Users className="w-4 h-4 text-emerald-400" /> Register Affiliate Ambassador
                  </h4>
                  <p className="text-xs text-slate-400">
                    Onboard existing solar owners to refer neighbors for cash commissions.
                  </p>
                </div>

                <form onSubmit={handleRegisterAmbassador} className="space-y-3">
                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">
                      Ambassador Full Name
                    </label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. Chief Chidi Obi"
                      value={newAmbName}
                      onChange={(e) => setNewAmbName(e.target.value)}
                      className="w-full bg-slate-900 border border-slate-800 rounded-xl px-3 py-2 text-xs font-semibold text-slate-200 focus:outline-none focus:border-emerald-500"
                    />
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">
                      Phone Number (WhatsApp)
                    </label>
                    <input
                      type="tel"
                      required
                      placeholder="e.g. +234 803 000 0000"
                      value={newAmbPhone}
                      onChange={(e) => setNewAmbPhone(e.target.value)}
                      className="w-full bg-slate-900 border border-slate-800 rounded-xl px-3 py-2 text-xs font-semibold text-slate-200 focus:outline-none focus:border-emerald-500"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">
                        System Sized
                      </label>
                      <select
                        value={newAmbSystem}
                        onChange={(e) => setNewAmbSystem(e.target.value)}
                        className="w-full bg-slate-900 border border-slate-800 rounded-xl px-3 py-2 text-xs font-semibold text-slate-200 focus:outline-none focus:border-emerald-500"
                      >
                        <option value="1.5kVA Standard">1.5kVA Standard</option>
                        <option value="3.5kVA / 5.0kWh Heavy-Duty">3.5kVA Heavy-Duty</option>
                        <option value="7.5kVA Premium">7.5kVA Premium</option>
                        <option value="10kVA Microgrid">10kVA Microgrid</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">
                        Bank Details
                      </label>
                      <input
                        type="text"
                        placeholder="Zenith, GTB etc."
                        value={newAmbBank}
                        onChange={(e) => setNewAmbBank(e.target.value)}
                        className="w-full bg-slate-900 border border-slate-800 rounded-xl px-3 py-2 text-xs font-semibold text-slate-200 focus:outline-none focus:border-emerald-500"
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    className="w-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-black text-xs py-3 rounded-xl shadow-lg shadow-emerald-500/10 transition-all uppercase tracking-wider"
                  >
                    Generate Ambassador Credentials
                  </button>
                </form>

                {referralSuccessMsg && (
                  <div className="p-3 bg-emerald-950/60 border border-emerald-500/30 rounded-xl text-emerald-400 text-xs font-semibold animate-pulse">
                    {referralSuccessMsg}
                  </div>
                )}
              </div>

              {/* Right Column: Ledger / Leaderboard */}
              <div className="lg:col-span-7 bg-slate-950 border border-slate-800 rounded-2xl p-6 space-y-4">
                <div className="flex justify-between items-center">
                  <div className="space-y-0.5">
                    <h4 className="text-sm font-bold text-white">Ambassador Commissions Ledger</h4>
                    <p className="text-xs text-slate-400">Track word-of-mouth audits, closed deals, and 2% payouts.</p>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs font-sans">
                    <thead>
                      <tr className="border-b border-slate-800 text-slate-400 font-bold uppercase tracking-wider">
                        <th className="pb-3 pr-2">Ambassador Name</th>
                        <th className="pb-3 px-2">Audits referred</th>
                        <th className="pb-3 px-2">Closed Sales</th>
                        <th className="pb-3 px-2">Pending Payout</th>
                        <th className="pb-3 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-800/60">
                      {ambassadors.map((amb) => (
                        <tr key={amb.id} className="hover:bg-slate-900/40 transition-colors">
                          <td className="py-3 pr-2">
                            <div className="font-bold text-slate-200">{amb.name}</div>
                            <div className="text-[10px] text-slate-400">{amb.system} • {amb.phone}</div>
                          </td>
                          <td className="py-3 px-2 font-mono font-bold text-slate-300">
                            {amb.audits}
                          </td>
                          <td className="py-3 px-2">
                            <span className="px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 text-[10px] font-bold border border-emerald-500/20">
                              {amb.sales} closed
                            </span>
                          </td>
                          <td className="py-3 px-2 font-mono font-bold text-emerald-400">
                            ₦{amb.pendingPayout.toLocaleString()}
                          </td>
                          <td className="py-3 text-right">
                            <div className="flex items-center justify-end gap-1.5 flex-wrap">
                              <button
                                onClick={() => handleCopyLink(amb.id)}
                                className={`px-2 py-1 rounded-lg text-[10px] font-black uppercase transition-all flex items-center gap-1 ${
                                  copiedAmbId === amb.id
                                    ? 'bg-emerald-500 text-slate-950'
                                    : 'bg-slate-900 text-slate-300 hover:bg-slate-800 border border-slate-800'
                                }`}
                              >
                                {copiedAmbId === amb.id ? 'Copied' : 'Copy Link'}
                              </button>
                              <button
                                onClick={() => handleRecordReferralSale(amb.id)}
                                className="px-2 py-1 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 border border-emerald-500/30 rounded-lg text-[10px] font-black uppercase"
                              >
                                Record Sale
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {activeExpansionTab === 'outage' && (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 animate-fadeIn">
              {/* Left Column: Grid Live Map Monitor */}
              <div className="lg:col-span-5 bg-slate-950 border border-slate-800 rounded-2xl p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <h4 className="text-sm font-bold text-white flex items-center gap-2">
                      <Zap className="text-amber-400 w-4 h-4" /> Live Grid Monitor & Ad Overrides
                    </h4>
                    <p className="text-xs text-slate-400">
                      Query real-time grid status to instantly trigger automated local Google & Meta budget multipliers.
                    </p>
                  </div>
                  <button
                    onClick={handleResetBlackouts}
                    className="text-xs text-slate-400 hover:text-white underline font-bold"
                  >
                    Reset Grid
                  </button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {blackoutZones.map((zone) => (
                    <div
                      key={zone.id}
                      className={`p-3 rounded-xl border flex flex-col justify-between space-y-2 transition-all ${
                        zone.activeBlackout
                          ? 'bg-amber-950/20 border-amber-500/40'
                          : 'bg-slate-900 border-slate-800'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-bold text-white text-xs">{zone.name}</span>
                        <span className={`w-2 h-2 rounded-full ${zone.activeBlackout ? 'bg-amber-400 animate-ping' : 'bg-emerald-400'}`} />
                      </div>
                      <div className="flex justify-between items-center">
                        <span className={`text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded ${
                          zone.activeBlackout ? 'bg-amber-500/10 text-amber-300 border border-amber-500/20' : 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                        }`}>
                          {zone.status}
                        </span>
                        {zone.activeBlackout && (
                          <span className="text-[10px] text-amber-400 font-mono font-bold">
                            {zone.adBidMultiplier}x Bid
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="space-y-3 pt-2">
                  <div className="flex justify-between items-center">
                    <label className="text-[10px] font-black uppercase tracking-wider text-slate-400">
                      Target Blackout Multiplier
                    </label>
                    <span className="text-xs font-mono font-bold text-amber-400">{blackoutBidMultiplier}x</span>
                  </div>
                  <input
                    type="range"
                    min="1.5"
                    max="3.5"
                    step="0.1"
                    value={blackoutBidMultiplier}
                    onChange={(e) => setBlackoutBidMultiplier(Number(e.target.value))}
                    className="w-full h-1 bg-slate-800 rounded-lg appearance-none cursor-pointer accent-amber-500"
                  />
                </div>

                <button
                  onClick={handleExecuteGridOutagesSync}
                  disabled={isSyncingGridOutages}
                  className="w-full bg-amber-500 hover:bg-amber-400 text-slate-950 font-black text-xs py-3.5 rounded-xl shadow-lg shadow-amber-500/10 transition-all uppercase tracking-wider flex items-center justify-center gap-1.5"
                >
                  {isSyncingGridOutages ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Syncing Real-Time Grid Outage API...
                    </>
                  ) : (
                    <>
                      <Zap className="w-4 h-4 fill-current animate-bounce" />
                      Sync & Execute Regional Grid Outage Multipliers ⚡
                    </>
                  )}
                </button>
              </div>

              {/* Right Column: Google & Meta Ads API bid stream */}
              <div className="lg:col-span-7 bg-slate-950 border border-slate-800 rounded-2xl p-6 space-y-4">
                <div className="space-y-1">
                  <h4 className="text-sm font-bold text-white flex items-center gap-2">
                    <Radio className="w-4 h-4 text-amber-400" /> API Bid Logs Stream
                  </h4>
                  <p className="text-xs text-slate-400">Real-time API feedback as budget bid multipliers align dynamically.</p>
                </div>

                <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 h-64 overflow-y-auto font-mono text-[11px] text-slate-300 space-y-2 select-all">
                  {blackoutLogs.map((log, index) => (
                    <div key={index} className="leading-relaxed border-b border-slate-800/40 pb-1 last:border-0">
                      {log}
                    </div>
                  ))}
                </div>

                <div className="p-3 bg-slate-900 border border-slate-800 rounded-xl">
                  <span className="block text-[10px] font-black uppercase tracking-wider text-slate-400 mb-1">
                    Bidding Override Target Keywords
                  </span>
                  <p className="text-xs text-amber-300 font-mono italic">
                    "{blackoutKeywords}"
                  </p>
                </div>
              </div>
            </div>
          )}

          {activeExpansionTab === 'whatsapp' && (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 animate-fadeIn">
              {/* Left Column: Leads selector */}
              <div className="lg:col-span-5 bg-slate-950 border border-slate-800 rounded-2xl p-6 space-y-4">
                <div className="space-y-1">
                  <h4 className="text-sm font-bold text-white flex items-center gap-2">
                    <MessageSquare className="w-4 h-4 text-emerald-400" /> Active Lead Nurture Queue
                  </h4>
                  <p className="text-xs text-slate-400">Select a cold customer lead to deploy conversational audio pitches.</p>
                </div>

                <div className="space-y-2">
                  {nurtureLeads.map((lead) => (
                    <div
                      key={lead.id}
                      onClick={() => setSelectedNurtureId(lead.id)}
                      className={`p-3 rounded-xl border cursor-pointer transition-all flex items-center justify-between ${
                        selectedNurtureId === lead.id
                          ? 'bg-emerald-950/20 border-emerald-500/40'
                          : 'bg-slate-900 border-slate-800 hover:border-slate-700'
                      }`}
                    >
                      <div className="space-y-0.5">
                        <div className="font-bold text-white text-xs">{lead.name}</div>
                        <div className="text-[10px] text-slate-400">{lead.location} • Gen Cost: ₦{lead.fuelCost.toLocaleString()}/day</div>
                      </div>
                      <div className="text-right space-y-1">
                        <span className={`text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded ${
                          lead.status.includes('Hot') ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : 'bg-slate-800 text-slate-400'
                        }`}>
                          {lead.status}
                        </span>
                        <div className="text-[10px] text-slate-400 font-mono">{lead.likelihood}% conversion</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right Column: Waveform synthesizer & WhatsApp Actions */}
              <div className="lg:col-span-7 bg-slate-950 border border-slate-800 rounded-2xl p-6 space-y-4">
                {(() => {
                  const activeLead = nurtureLeads.find(l => l.id === selectedNurtureId);
                  if (!activeLead) return <p className="text-xs text-slate-400">Select a lead to begin.</p>;

                  const rawMessage = whatsappTemplate === 'tariff_band'
                    ? `Hello ${activeLead.name},\n\nThis is the Senior Technical Director at Access Technologies.\n\nWith the recent grid instability and tariff hikes, grid reliance is draining capital. By installing our custom ${activeLead.sizing} Solar Microgrid, you can hedge your property and safeguard your operations.\n\nI have prepared a Net Present Value (NPV) savings amortization breakdown for you. Let's schedule a brief 10-minute engineering survey?`
                    : `Hello ${activeLead.name},\n\nI am the Senior Technical Director at Access Technologies and Renewable Energy.\n\nOur system engineers analyzed your load parameters and determined you can offset ₦${Math.round(activeLead.fuelCost * 30 * 12 * 0.85).toLocaleString()} annually by upgrading to our premium ${activeLead.sizing} Solar Microgrid.\n\nI just prepared a boardroom-ready proposal and left you a voice breakdown of your payback schedule. Would you be free for a 10-minute design session?`;
                  const encodedMessage = encodeURIComponent(rawMessage);
                  const waUrl = `https://wa.me/${activeLead.phone.replace(/[^0-9]/g, '')}?text=${encodedMessage}`;

                  return (
                    <div className="space-y-4">
                      <div className="space-y-1">
                        <div className="flex justify-between items-center">
                          <h4 className="font-bold text-white text-sm">Conversational Voice Synthesis Hub</h4>
                          <span className="text-[11px] text-slate-400 font-bold">{activeLead.name}</span>
                        </div>
                        <p className="text-xs text-slate-400">
                          Deploy custom calculated solar proposals and an automated high-fidelity engineer voice memo drop directly to WhatsApp.
                        </p>
                      </div>

                      {/* Template Selector */}
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          onClick={() => setWhatsappTemplate('fuel_savings')}
                          className={`py-2 px-3 rounded-xl font-bold text-[10px] transition-all border ${whatsappTemplate === 'fuel_savings' ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30' : 'bg-slate-900 text-slate-400 border-slate-800'}`}
                        >
                          Generator Fuel Offset
                        </button>
                        <button
                          onClick={() => setWhatsappTemplate('tariff_band')}
                          className={`py-2 px-3 rounded-xl font-bold text-[10px] transition-all border ${whatsappTemplate === 'tariff_band' ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30' : 'bg-slate-900 text-slate-400 border-slate-800'}`}
                        >
                          Band A Grid Tariff Offset
                        </button>
                      </div>

                      <div className="p-4 bg-slate-900 rounded-xl border border-slate-800">
                        <span className="block text-[10px] font-black uppercase tracking-wider text-slate-400 mb-1">
                          Generated WhatsApp Payload Preview
                        </span>
                        <p className="text-xs text-slate-300 leading-relaxed font-sans bg-slate-950 p-3 rounded-lg whitespace-pre-wrap">
                          {rawMessage}
                        </p>
                      </div>

                      {/* Voice Synthesis Module */}
                      <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] font-black uppercase tracking-wider text-slate-400">
                            Engineering Voice Memo wave synthesizer
                          </span>
                          {activeLead.voiceSent ? (
                            <span className="text-[10px] text-emerald-400 font-bold flex items-center gap-1">
                              ✓ Voice Memo Sent
                            </span>
                          ) : (
                            <span className="text-[10px] text-amber-400 font-bold animate-pulse">
                              Pending Dispatch
                            </span>
                          )}
                        </div>

                        {/* Interactive Waveform visual */}
                        <div className="h-12 bg-slate-950 rounded-xl p-3 flex items-center justify-between gap-1 overflow-hidden font-mono">
                          {Array.from({ length: 42 }, (_, i) => {
                            const heights = [20, 30, 40, 10, 5, 25, 35, 45, 12, 18, 28, 48, 8, 32, 22, 15, 38, 42, 28, 10, 14, 24, 34, 44, 20, 30, 40, 10, 25, 35, 45, 12, 18, 28, 48, 8, 32, 22, 15, 38, 42, 28];
                            const height = heights[i % heights.length];
                            return (
                              <div
                                key={i}
                                className={`w-1 rounded-full transition-all duration-300 ${
                                  isSynthesizingVoice
                                    ? 'bg-emerald-500 animate-pulse'
                                    : activeLead.voiceSent
                                    ? 'bg-emerald-700'
                                    : 'bg-slate-800'
                                }`}
                                style={{ height: `${height}%` }}
                              />
                            );
                          })}
                        </div>

                        {isSynthesizingVoice ? (
                          <div className="text-xs text-emerald-400 font-mono font-bold animate-pulse flex items-center gap-2 justify-center py-1">
                            <RefreshCw className="w-4 h-4 animate-spin" />
                            {voiceSynthesisStep}
                          </div>
                        ) : (
                          <div className="flex gap-2">
                            <button
                              onClick={handleSynthesizeVoice}
                              className="flex-1 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-black text-xs py-3 rounded-xl transition-all uppercase tracking-wider flex items-center justify-center gap-2"
                            >
                              <Volume2 className="w-4 h-4" /> Synthesize Voice Memo
                            </button>
                            <a
                              href={waUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="bg-slate-950 hover:bg-slate-800 text-emerald-400 border border-emerald-500/30 font-black text-xs px-4 py-3 rounded-xl transition-all uppercase tracking-wider flex items-center justify-center gap-1.5"
                            >
                              Launch Direct Chat <ArrowUpRight className="w-4 h-4" />
                            </a>
                          </div>
                        )}
                      </div>

                      <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
                        <span className="block text-[10px] font-black uppercase tracking-wider text-slate-400 mb-1.5">
                          WhatsApp Dispatch Telemetry Logs
                        </span>
                        <div className="h-20 overflow-y-auto font-mono text-[10px] text-slate-400 space-y-1">
                          {nurtureLogs.length === 0 ? (
                            <div className="italic text-slate-600">No telemetry logs dispatched yet in this session.</div>
                          ) : (
                            nurtureLogs.map((log, index) => <div key={index}>{log}</div>)
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          )}

          {activeExpansionTab === 'b2b' && (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 animate-fadeIn">
              {/* Left Column: Input Form & Basic Stats */}
              <div className="lg:col-span-5 bg-slate-950 border border-slate-800 rounded-2xl p-6 space-y-4">
                <div className="space-y-1">
                  <h4 className="text-sm font-bold text-white flex items-center gap-2">
                    <Sliders className="w-4 h-4 text-cyan-400" /> B2B Sizing Parameters
                  </h4>
                  <p className="text-xs text-slate-400">
                    Input diesel generator runtime overheads to calculate dynamic ROI and CapEx amortization years.
                  </p>
                </div>

                <div className="space-y-3 font-sans">
                  <div>
                    <div className="flex justify-between items-center mb-1">
                      <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                        Monthly Diesel Consumed (Liters)
                      </label>
                      <span className="text-xs font-mono font-bold text-white">{monthlyDieselLiters.toLocaleString()} L</span>
                    </div>
                    <input
                      type="range"
                      min="200"
                      max="3000"
                      step="50"
                      value={monthlyDieselLiters}
                      onChange={(e) => setMonthlyDieselLiters(Number(e.target.value))}
                      className="w-full h-1 bg-slate-800 rounded-lg appearance-none cursor-pointer accent-cyan-500"
                    />
                  </div>

                  <div>
                    <div className="flex justify-between items-center mb-1">
                      <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                        Diesel Price per Liter (₦)
                      </label>
                      <span className="text-xs font-mono font-bold text-emerald-400">₦{dieselPricePerLiter.toLocaleString()} / L</span>
                    </div>
                    <input
                      type="range"
                      min="1100"
                      max="1500"
                      step="10"
                      value={dieselPricePerLiter}
                      onChange={(e) => setDieselPricePerLiter(Number(e.target.value))}
                      className="w-full h-1 bg-slate-800 rounded-lg appearance-none cursor-pointer accent-cyan-500"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">
                        Gen Daily Runtime
                      </label>
                      <input
                        type="number"
                        value={generatorRuntimeHours}
                        onChange={(e) => setGeneratorRuntimeHours(Number(e.target.value))}
                        className="w-full bg-slate-900 border border-slate-800 rounded-xl px-3 py-2 text-xs font-semibold text-slate-200 focus:outline-none focus:border-cyan-500"
                      />
                    </div>

                    <div>
                      <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">
                        Target Solar Sizing
                      </label>
                      <select
                        value={b2bSolarMicrogridKva}
                        onChange={(e) => setB2bSolarMicrogridKva(Number(e.target.value))}
                        className="w-full bg-slate-900 border border-slate-800 rounded-xl px-3 py-2 text-xs font-semibold text-slate-200 focus:outline-none focus:border-cyan-500"
                      >
                        <option value={10}>10kVA Microgrid</option>
                        <option value={20}>20kVA Turnkey</option>
                        <option value={35}>35kVA Enterprise</option>
                        <option value={50}>50kVA C&I Mega</option>
                      </select>
                    </div>
                  </div>

                  {(() => {
                    const b2bMicrogridCost = b2bSolarMicrogridKva * 850000;
                    const initialDieselExpense = monthlyDieselLiters * dieselPricePerLiter * 12;
                    const amortizationVal = initialDieselExpense > 0 ? (b2bMicrogridCost / initialDieselExpense * 12) : 0;
                    return (
                      <div className="p-4 bg-slate-900 border border-slate-800 rounded-xl space-y-2">
                        <div className="flex justify-between items-center text-xs">
                          <span className="text-slate-400">Turnkey Solar CapEx:</span>
                          <span className="font-bold font-mono text-white">₦{b2bMicrogridCost.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between items-center text-xs">
                          <span className="text-slate-400">Annual Diesel Fuel Cost:</span>
                          <span className="font-bold font-mono text-amber-400">₦{initialDieselExpense.toLocaleString()}</span>
                        </div>
                        <div className="border-t border-slate-800 my-1 pt-1 flex justify-between items-center text-xs font-black">
                          <span className="text-emerald-400">Amortization Term:</span>
                          <span className="font-mono text-emerald-300">
                            {amortizationVal.toFixed(1)} Months
                          </span>
                        </div>
                      </div>
                    );
                  })()}

                  <button
                    onClick={handleGenerateProposal}
                    disabled={isGeneratingProposal}
                    className="w-full bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-black text-xs py-3 rounded-xl shadow-lg shadow-cyan-500/10 transition-all uppercase tracking-wider flex items-center justify-center gap-1.5"
                  >
                    {isGeneratingProposal ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        Generating PDF Document Proposal...
                      </>
                    ) : (
                      <>
                        <Download className="w-4 h-4" />
                        Generate Boardroom Proposal
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Right Column: Recharts comparison and executive summary download */}
              <div className="lg:col-span-7 bg-slate-950 border border-slate-800 rounded-2xl p-6 space-y-4">
                <div className="space-y-1">
                  <h4 className="text-sm font-bold text-white flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-cyan-400" /> 10-Year Amortization & Cash Savings Curve
                  </h4>
                  <p className="text-xs text-slate-400">Visualizing 10-year diesel maintenance inflation vs stable modern B2B solar.</p>
                </div>

                {(() => {
                  const b2bMicrogridCost = b2bSolarMicrogridKva * 850000;
                  const initialDieselExpense = monthlyDieselLiters * dieselPricePerLiter * 12;

                  const chartData = Array.from({ length: 10 }, (_, i) => {
                    const year = i + 1;
                    let cumulativeDiesel = 0;
                    let dieselCostYearly = initialDieselExpense;
                    for (let y = 0; y < year; y++) {
                      cumulativeDiesel += dieselCostYearly + 500000;
                      dieselCostYearly *= 1.08; 
                    }
                    const cumulativeSolar = b2bMicrogridCost + (year > 1 ? (year - 1) * 150000 : 0);

                    return {
                      name: `Yr ${year}`,
                      'Diesel Gen (Fuel + Maint)': Math.round(cumulativeDiesel),
                      'Turnkey B2B Solar': Math.round(cumulativeSolar),
                    };
                  });

                  return (
                    <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
                      <ResponsiveContainer width="100%" height={240}>
                        <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 20, bottom: 0 }}>
                          <defs>
                            <linearGradient id="colorDiesel" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.2}/>
                              <stop offset="95%" stopColor="#f59e0b" stopOpacity={0}/>
                            </linearGradient>
                            <linearGradient id="colorSolar" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#10b981" stopOpacity={0.2}/>
                              <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                          <XAxis dataKey="name" stroke="#64748b" fontSize={10} />
                          <YAxis 
                            stroke="#64748b" 
                            fontSize={10} 
                            tickFormatter={(v) => `₦${(v / 1000000).toFixed(1)}M`}
                          />
                          <Tooltip 
                            contentStyle={{ backgroundColor: '#020617', borderColor: '#334155', borderRadius: '12px', fontSize: '11px' }}
                            formatter={(v: any) => [`₦${Number(v).toLocaleString()}`, '']}
                          />
                          <Legend wrapperStyle={{ fontSize: '10px', marginTop: '10px' }} />
                          <Area type="monotone" dataKey="Diesel Gen (Fuel + Maint)" stroke="#f59e0b" fillOpacity={1} fill="url(#colorDiesel)" strokeWidth={2} />
                          <Area type="monotone" dataKey="Turnkey B2B Solar" stroke="#10b981" fillOpacity={1} fill="url(#colorSolar)" strokeWidth={2} />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  );
                })()}

                {proposalPdfReady && (
                  <div className="p-4 bg-cyan-950/20 border border-cyan-500/30 rounded-xl space-y-2 animate-fadeIn">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-cyan-300 font-bold flex items-center gap-1.5">
                        <CheckCircle2 className="w-4 h-4 text-cyan-400" /> Executive Board Proposal Generated
                      </span>
                      <button
                        onClick={() => window.print()}
                        className="text-[10px] text-cyan-400 hover:text-cyan-300 underline font-black uppercase"
                      >
                        Print Proposal
                      </button>
                    </div>
                    <div className="bg-slate-950 p-3 rounded-lg border border-slate-800 text-[11px] text-slate-300 leading-relaxed font-sans">
                      <div className="font-extrabold text-white text-xs mb-1">PROPOSAL SUMMARY: SIZED FOR {b2bSolarMicrogridKva}kVA TURNKEY MICROGRID</div>
                      <div>- **Sponsor**: Access Technologies and Renewable Energy Ltd.</div>
                      <div>- **Annual Generator Offset**: ₦{(monthlyDieselLiters * dieselPricePerLiter * 12 * 0.9).toLocaleString()} saved annually.</div>
                      <div>- **Diesel Payback schedule**: Amortization in just **{((b2bSolarMicrogridKva * 850000) / (monthlyDieselLiters * dieselPricePerLiter * 12) * 12).toFixed(1)} months**!</div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeExpansionTab === 'router' && (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 animate-fadeIn">
              {/* Left Column: Scheduled Site Audits */}
              <div className="lg:col-span-5 bg-slate-950 border border-slate-800 rounded-2xl p-6 space-y-4">
                <div className="space-y-1">
                  <h4 className="text-sm font-bold text-white flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-indigo-400" /> Pending Site Audits (Umuahia)
                  </h4>
                  <p className="text-xs text-slate-400">Scheduled home & office engineering site assessments in Abia State.</p>
                </div>

                <div className="space-y-2">
                  {auditLocations.map((loc) => {
                    const matchedInSeq = optimizedSequence.find(o => o.id === loc.id);
                    return (
                      <div
                        key={loc.id}
                        className="p-3 bg-slate-900 border border-slate-800 rounded-xl flex items-center justify-between"
                      >
                        <div className="space-y-0.5">
                          <div className="font-bold text-white text-xs">{loc.client}</div>
                          <p className="text-[10px] text-slate-400">{loc.address}</p>
                          <span className="inline-block text-[9px] font-black uppercase tracking-wider text-slate-400 mt-1">
                            Sized: {loc.packageSize}
                          </span>
                        </div>
                        {matchedInSeq && matchedInSeq.sequence > 0 ? (
                          <span className="w-6 h-6 rounded-full bg-indigo-600 text-white flex items-center justify-center font-black text-xs shadow-md">
                            {matchedInSeq.sequence}
                          </span>
                        ) : (
                          <span className="w-6 h-6 rounded-full bg-slate-800 text-slate-500 flex items-center justify-center font-bold text-xs">
                            -
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>

                <button
                  onClick={handleOptimizeRoute}
                  disabled={isOptimizingRoute}
                  className="w-full bg-indigo-600 hover:bg-indigo-500 text-white font-black text-xs py-3.5 rounded-xl shadow-lg shadow-indigo-500/10 transition-all uppercase tracking-wider flex items-center justify-center gap-1.5"
                >
                  {isOptimizingRoute ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Sequencing Spatial Coordinates...
                    </>
                  ) : (
                    <>
                      <Compass className="w-4 h-4 animate-spin-slow" />
                      Optimize Route & Dispatch Teams
                    </>
                  )}
                </button>
              </div>

              {/* Right Column: Visual Route Map and Optimized Path list */}
              <div className="lg:col-span-7 bg-slate-950 border border-slate-800 rounded-2xl p-6 space-y-4">
                <div className="space-y-1">
                  <div className="flex justify-between items-center">
                    <h4 className="text-sm font-bold text-white flex items-center gap-2">
                      <Navigation className="w-4 h-4 text-indigo-400" /> Route Sequential Itinerary
                    </h4>
                    {routeOptimizationScore > 0 && (
                      <span className="px-2 py-0.5 rounded-full bg-indigo-500/20 text-indigo-300 text-[10px] font-extrabold border border-indigo-500/30">
                        ⚡ Logistics Boost: {routeOptimizationScore}%
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-400">Sequence calculated to minimize travel overhead, road traffic, and diesel fuel consumed.</p>
                </div>

                {optimizedSequence.length > 0 ? (
                  <div className="space-y-4">
                    {/* Graphical Route representation */}
                    <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 flex flex-col sm:flex-row items-center justify-between gap-2">
                      {optimizedSequence.map((stop, idx) => (
                        <React.Fragment key={stop.id}>
                          <div className="flex items-center gap-2.5">
                            <div className="w-8 h-8 rounded-full bg-indigo-600 text-white font-black text-xs flex items-center justify-center shadow-lg shadow-indigo-500/20">
                              {stop.sequence}
                            </div>
                            <div className="text-left">
                              <div className="font-extrabold text-slate-200 text-xs">{stop.client.split(' ')[1]}</div>
                              <div className="text-[9px] text-slate-400 truncate max-w-[100px]">{stop.address.split(',')[0]}</div>
                            </div>
                          </div>
                          {idx < optimizedSequence.length - 1 && (
                            <div className="hidden sm:block flex-1 h-0.5 border-t border-dashed border-slate-700 mx-2" />
                          )}
                        </React.Fragment>
                      ))}
                    </div>

                    <div className="space-y-2">
                      <span className="block text-[10px] font-black uppercase tracking-wider text-slate-400">
                        Step-by-Step Optimized Sequence Dispatch
                      </span>
                      <div className="space-y-1.5 font-sans">
                        {optimizedSequence.map((stop) => (
                          <div key={stop.id} className="flex items-center gap-3 bg-slate-900/60 p-2.5 rounded-xl border border-slate-800/80">
                            <span className="text-[10px] font-black text-indigo-400 font-mono w-4">#{stop.sequence}</span>
                            <div className="text-xs flex-1">
                              <span className="font-bold text-white">{stop.client}</span>
                              <span className="text-slate-400"> • {stop.address}</span>
                            </div>
                            <span className="text-[10px] text-slate-500 font-mono font-bold">Stop</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="h-44 bg-slate-900 border border-slate-800 rounded-xl flex flex-col items-center justify-center text-center p-4">
                    <Compass className="w-10 h-10 text-slate-600 mb-2 animate-bounce" />
                    <p className="text-xs text-slate-400 max-w-xs leading-relaxed">
                      Click **"Optimize Route & Dispatch Teams"** to cluster locations and render the optimized site audit path.
                    </p>
                  </div>
                )}

                <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
                  <span className="block text-[10px] font-black uppercase tracking-wider text-slate-400 mb-1.5">
                    Optimization Console Outputs
                  </span>
                  <div className="h-20 overflow-y-auto font-mono text-[10px] text-slate-400 space-y-1">
                    {optimizationLogs.length === 0 ? (
                      <div className="italic text-slate-600">Pending optimization dispatch signal.</div>
                    ) : (
                      optimizationLogs.map((log, index) => <div key={index}>{log}</div>)
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* CAPI Postback Status Banner */}
      {capiSuccessMessage && (
        <div className="bg-emerald-950/60 border border-emerald-500/30 rounded-2xl p-4 flex items-center gap-3 text-emerald-300 font-sans text-xs font-bold animate-pulse shadow-lg">
          <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0" />
          <span>{capiSuccessMessage}</span>
        </div>
      )}

      {/* AI GROWTH COMMAND CENTER & REAL-TIME CAPI POSTBACK BENTO BOX */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        
        {/* Left Bento: AI Autopilot Co-Pilot Command Center */}
        <div className="lg:col-span-7 bg-slate-900 border border-slate-800 rounded-3xl p-6 sm:p-8 shadow-xl space-y-6 flex flex-col justify-between relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/5 rounded-full filter blur-3xl pointer-events-none"></div>
          
          <div className="space-y-1.5 relative z-10">
            <div className="flex items-center gap-2">
              <span className="flex h-2.5 w-2.5 relative">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
              </span>
              <span className="text-[10px] font-black uppercase tracking-widest text-emerald-400">
                AI Autopilot Smart Feed
              </span>
            </div>
            <h3 className="text-lg font-black text-white flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-amber-400" /> AI Growth Co-Pilot Optimizer
            </h3>
            <p className="text-xs text-slate-400 leading-relaxed">
              Our real-time market listener monitors macro-economic variables in Nigeria (grid stability, fuel price fluctuations, and blackout maps) and generates high-ROAS budget reallocation recommendations.
            </p>
          </div>

          <div className="space-y-4 my-4">
            {aiOptimizations.map((opt) => (
              <div
                key={opt.id}
                className={`bg-slate-950 border rounded-2xl p-4 transition-all duration-300 ${opt.applied ? 'border-emerald-500/20 bg-emerald-950/5' : 'border-slate-800 hover:border-slate-700'}`}
              >
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-extrabold text-white text-xs sm:text-sm">{opt.title}</h4>
                      <span className={`text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full ${opt.applied ? 'bg-emerald-500/15 text-emerald-400' : 'bg-red-500/10 text-red-400 border border-red-500/20'}`}>
                        {opt.metric}
                      </span>
                    </div>
                    <p className="text-xs text-slate-400 leading-relaxed max-w-xl">{opt.desc}</p>
                  </div>

                  <button
                    onClick={() => handleExecuteOptimization(opt.id)}
                    disabled={opt.applied}
                    className={`px-4 py-2.5 rounded-xl font-bold text-xs transition-all uppercase tracking-wider shrink-0 sm:self-center ${opt.applied ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/20 cursor-default' : 'bg-gradient-to-r from-amber-500 to-emerald-500 text-slate-950 hover:brightness-110 font-black'}`}
                  >
                    {opt.applied ? '✓ Strategy Deployed' : opt.actionLabel}
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="text-[10px] text-slate-500 italic relative z-10">
            *Deploying strategies updates campaign parameters, budget distribution, and synchronizes real-time bidding flags in your active ad sets.
          </div>
        </div>

        {/* Right Bento: CRM Closed-Won Sales CAPI Dispatch Console */}
        <div className="lg:col-span-5 bg-slate-900 border border-slate-800 rounded-3xl p-6 sm:p-8 shadow-xl flex flex-col justify-between relative overflow-hidden">
          <div className="absolute top-0 left-0 w-64 h-64 bg-cyan-500/5 rounded-full filter blur-3xl pointer-events-none"></div>

          <div className="space-y-1.5 relative z-10 mb-4">
            <span className="text-[10px] font-black uppercase tracking-widest text-cyan-400 block">
              High-Conversion Development Console
            </span>
            <h3 className="text-lg font-black text-white flex items-center gap-2">
              <Zap className="w-5 h-5 text-cyan-400" /> CRM Real-Time CAPI Postback
            </h3>
            <p className="text-xs text-slate-400 leading-relaxed">
              When sales teams close a solar contract offline, immediately post the conversion value back to Google & Meta algorithms. This feeds real-time high-value signals to ad networks.
            </p>
          </div>

          <form onSubmit={handleCapiPostback} className="space-y-4 relative z-10">
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-300 mb-1.5">
                Select Active Lead / Customer
              </label>
              <select
                value={selectedLead}
                onChange={(e) => handleLeadChange(e.target.value)}
                className="w-full bg-slate-950 border border-slate-700/80 rounded-xl px-3 py-2 text-xs font-semibold text-slate-200 focus:outline-none focus:border-cyan-500"
              >
                {getHotLeadsList().map((lead) => (
                  <option key={lead.name} value={lead.name}>
                    {lead.name === 'CUSTOM_ENTRY' ? 'Custom Customer Entry...' : `${lead.name} (${lead.location})`}
                  </option>
                ))}
              </select>
            </div>

            {selectedLead === 'CUSTOM_ENTRY' && (
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-300 mb-1">
                  Customer Name
                </label>
                <input
                  type="text"
                  placeholder="e.g. Alhaji Chinedu"
                  value={customLeadName}
                  onChange={(e) => setCustomLeadName(e.target.value)}
                  className="w-full bg-slate-950 border border-slate-700/80 rounded-xl px-3 py-2 text-xs font-semibold text-slate-200 focus:outline-none focus:border-cyan-500"
                />
              </div>
            )}

            <div>
              <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-300 mb-1.5">
                Sized System Package
              </label>
              <select
                value={selectedPackage}
                onChange={(e) => handlePackageChange(e.target.value)}
                className="w-full bg-slate-950 border border-slate-700/80 rounded-xl px-3 py-2 text-xs font-semibold text-slate-200 focus:outline-none focus:border-cyan-500"
              >
                <option value="1.5kVA / 2.5kWh Lite (₦1,250,000)">1.5kVA / 2.5kWh Lite (₦1,250,000)</option>
                <option value="3.5kVA / 5.0kWh Heavy-Duty (₦2,850,000)">3.5kVA / 5.0kWh Heavy-Duty (₦2,850,000)</option>
                <option value="7.5kVA / 10.0kWh Premium Duplex (₦6,450,000)">7.5kVA / 10.0kWh Premium Duplex (₦6,450,000)</option>
              </select>
            </div>

            <div>
              <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-300 mb-1">
                Final Closed Contract Value (NGN)
              </label>
              <input
                type="number"
                value={customContractValue}
                onChange={(e) => setCustomContractValue(Number(e.target.value))}
                className="w-full bg-slate-950 border border-slate-700/80 rounded-xl px-3 py-2 text-xs font-bold text-emerald-400 focus:outline-none focus:border-cyan-500 font-mono"
              />
            </div>

            <div>
              <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-300 mb-1.5">
                Target Ad Engine Pipeline
              </label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setSelectedPostbackPlatform('META_CAPI')}
                  className={`py-2 px-3 rounded-xl font-bold text-[10px] transition-all border ${selectedPostbackPlatform === 'META_CAPI' ? 'bg-cyan-500/20 text-cyan-300 border-cyan-500/40' : 'bg-slate-950 text-slate-400 border-slate-800 hover:border-slate-700'}`}
                >
                  Meta CAPI Pixel
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedPostbackPlatform('GOOGLE_CONVERSION_API')}
                  className={`py-2 px-3 rounded-xl font-bold text-[10px] transition-all border ${selectedPostbackPlatform === 'GOOGLE_CONVERSION_API' ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40' : 'bg-slate-950 text-slate-400 border-slate-800 hover:border-slate-700'}`}
                >
                  Google Enhanced
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={isPostingCapi}
              className="w-full bg-gradient-to-r from-cyan-500 to-emerald-500 hover:brightness-110 text-slate-950 font-black text-xs py-3.5 rounded-xl shadow-lg shadow-cyan-500/10 transition-all flex items-center justify-center gap-1.5"
            >
              {isPostingCapi ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Syncing Secure Postback Event...
                </>
              ) : (
                <>
                  <Zap className="w-4 h-4 fill-current" />
                  Dispatch Offline CAPI Postback
                </>
              )}
            </button>
          </form>
        </div>

      </div>

      {/* Regional Blackout Preset Quick Launch Panel */}
      <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 sm:p-8 shadow-xl space-y-4">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
              <Flame className="w-5 h-5 text-amber-400" /> High-Intent Audience Segment Presets (Blackout & Tariff Zones)
            </h3>
            <p className="text-xs text-slate-400 mt-0.5">
              Instant 1-click launch targeted at specific Nigerian fuel price spike corridors.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {NIGERIAN_META_TARGETING_PRESETS.map((preset) => (
            <div
              key={preset.id}
              className="bg-slate-950 border border-slate-800 rounded-2xl p-5 hover:border-emerald-500/60 transition-all flex flex-col justify-between space-y-3 group"
            >
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                    High Conversion
                  </span>
                  <span className="text-xs text-slate-400 font-mono font-bold">₦{preset.recommendedBudgetNgn.toLocaleString()}/day</span>
                </div>
                <h4 className="font-bold text-white text-sm group-hover:text-emerald-400 transition-colors">
                  {preset.name}
                </h4>
                <p className="text-xs text-slate-400 flex items-center gap-1">
                  <MapPin className="w-3 h-3 text-slate-500 shrink-0" /> {preset.region}
                </p>
                <div className="text-[11px] text-amber-300 bg-slate-900 p-2 rounded-lg border border-slate-800 italic">
                  "{preset.hook}"
                </div>
              </div>

              <button
                onClick={() => {
                  setWizardPreset(preset.id);
                  setIsWizardOpen(true);
                }}
                className="w-full bg-slate-900 hover:bg-emerald-600 hover:text-slate-950 text-slate-200 font-bold py-2.5 rounded-xl text-xs transition-all flex items-center justify-center gap-1.5 border border-slate-800"
              >
                Launch This Segment <ArrowUpRight className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* AI Ad Campaign Generator */}
      <AICampaignGenerator onCampaignCreated={fetchGrowthMetrics} />

      {/* Active Ad Campaigns Table */}
      <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 sm:p-8 shadow-xl space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-emerald-400" /> Active & Historical Ad Campaigns
            </h3>
            <p className="text-xs text-slate-400 mt-0.5">
              Live campaign controls for budget adjustments, status toggles, and performance metrics.
            </p>
          </div>

          <button
            onClick={() => setIsWizardOpen(true)}
            className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 px-4 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider flex items-center gap-1.5 shadow-md"
          >
            <Plus className="w-4 h-4 stroke-[3]" /> Add Campaign
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead>
              <tr className="border-b border-slate-800 text-slate-400 font-bold uppercase tracking-wider">
                <th className="py-3.5 px-4">Campaign & Channel</th>
                <th className="py-3.5 px-4">Target Region</th>
                <th className="py-3.5 px-4">Status & Toggle</th>
                <th className="py-3.5 px-4">Daily Budget</th>
                <th className="py-3.5 px-4">Ad Spend</th>
                <th className="py-3.5 px-4">Leads & CPL</th>
                <th className="py-3.5 px-4">Solar Revenue</th>
                <th className="py-3.5 px-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/60 font-sans">
              {campaigns.map((camp) => (
                <tr key={camp.id} className="hover:bg-slate-800/40 transition-colors">
                  <td className="py-4 px-4">
                    <div className="font-bold text-white text-sm flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${camp.platform === 'GOOGLE_ADS' ? 'bg-emerald-400' : 'bg-cyan-400'}`} />
                      {camp.name}
                    </div>
                    <div className="text-[11px] text-slate-400 mt-0.5 flex items-center gap-2">
                      <span className="font-semibold text-slate-300">
                        {camp.platform === 'GOOGLE_ADS' ? 'Google Search / PMax' : 'Meta Feed & Reels'}
                      </span>
                      <span>• Clicks: {camp.clicks.toLocaleString()}</span>
                    </div>
                  </td>

                  <td className="py-4 px-4 font-medium text-slate-300">
                    <div className="flex items-center gap-1 text-xs">
                      <MapPin className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                      {camp.region}
                    </div>
                  </td>

                  <td className="py-4 px-4">
                    <button
                      onClick={() => handleToggleCampaign(camp.id, camp.status)}
                      disabled={actionLoadingId === camp.id}
                      className={`px-3 py-1.5 rounded-xl font-bold text-[11px] transition-all flex items-center gap-1.5 ${
                        camp.status === 'ACTIVE'
                          ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/30'
                          : 'bg-amber-500/20 text-amber-400 border border-amber-500/30 hover:bg-amber-500/30'
                      }`}
                    >
                      {camp.status === 'ACTIVE' ? (
                        <>
                          <span className="w-2 h-2 rounded-full bg-emerald-400 animate-ping" />
                          Active
                          <Pause className="w-3 h-3 ml-1 fill-current" />
                        </>
                      ) : (
                        <>
                          <span className="w-2 h-2 rounded-full bg-amber-400" />
                          Paused
                          <Play className="w-3 h-3 ml-1 fill-current" />
                        </>
                      )}
                    </button>
                  </td>

                  <td className="py-4 px-4 font-mono font-bold text-slate-200">
                    <div className="flex items-center gap-1">
                      <span>₦{camp.dailyBudgetNgn.toLocaleString()}</span>
                      <button
                        onClick={() => {
                          const val = prompt('Enter new daily budget in NGN:', String(camp.dailyBudgetNgn));
                          if (val && !isNaN(Number(val))) {
                            handleUpdateBudget(camp.id, Number(val));
                          }
                        }}
                        className="text-slate-500 hover:text-emerald-400 p-1"
                        title="Edit Daily Budget"
                      >
                        <Sliders className="w-3 h-3" />
                      </button>
                    </div>
                  </td>

                  <td className="py-4 px-4 font-mono font-bold text-slate-300">
                    ₦{camp.spendNgn.toLocaleString()}
                  </td>

                  <td className="py-4 px-4 space-y-0.5">
                    <div className="font-bold text-amber-400 text-sm">{camp.leadsCaptured} Leads</div>
                    <div className="text-[11px] text-slate-400 font-mono">
                      CPL: <span className="text-emerald-400 font-bold">₦{camp.cplNgn.toLocaleString()}</span>
                    </div>
                  </td>

                  <td className="py-4 px-4 space-y-0.5">
                    <div className="font-bold text-emerald-300 font-mono text-sm">
                      ₦{(camp.realizedRevenueNgn / 1000000).toFixed(2)}M
                    </div>
                    <div className="text-[10px] text-emerald-400 font-bold">
                      {camp.roas}x ROAS
                    </div>
                  </td>

                  <td className="py-4 px-4 text-right">
                    <button
                      onClick={() => handleToggleCampaign(camp.id, camp.status)}
                      className="text-xs text-slate-400 hover:text-white underline font-semibold"
                    >
                      {camp.status === 'ACTIVE' ? 'Pause' : 'Resume'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Offline Conversion Sync & CPL Attribution Telemetry Stream */}
      <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 sm:p-8 shadow-xl space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <div className="inline-flex items-center gap-1.5 px-3 py-0.5 rounded-full bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 text-[11px] font-bold mb-1">
              <Radio className="w-3 h-3 animate-pulse" /> Google Conversion API & Meta CAPI Postbacks
            </div>
            <h3 className="text-lg font-bold text-white">Server-Side Offline Conversion Event Telemetry</h3>
            <p className="text-xs text-slate-400 mt-0.5">
              Automatically posts offline closed deal values back to ad network bid engines to train lookalike audiences for high-value 3.5kVA & 7.5kVA customers.
            </p>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs font-sans">
            <thead>
              <tr className="border-b border-slate-800 text-slate-400 font-bold uppercase tracking-wider">
                <th className="py-3 px-4">Time & Network</th>
                <th className="py-3 px-4">CRM Event Trigger</th>
                <th className="py-3 px-4">Customer & Location</th>
                <th className="py-3 px-4">System Package</th>
                <th className="py-3 px-4">Event Value (NGN)</th>
                <th className="py-3 px-4 text-right">CAPI Sync Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/60">
              {conversionLogs.map((log) => (
                <tr key={log.id} className="hover:bg-slate-800/40 transition-colors">
                  <td className="py-3.5 px-4 font-mono text-slate-300">
                    <div className="font-bold text-white text-xs">{log.timestamp}</div>
                    <div className="text-[10px] text-slate-400">
                      {log.platform === 'META_CAPI' ? 'Meta Conversions API' : 'Google Enhanced Conversions'}
                    </div>
                  </td>

                  <td className="py-3.5 px-4">
                    <span className={`px-2.5 py-1 rounded-lg text-[10px] font-bold ${
                      log.eventType === 'SolarSystemPurchase'
                        ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                        : log.eventType === 'SiteAuditBooked'
                        ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/30'
                        : 'bg-amber-500/20 text-amber-300 border border-amber-500/30'
                    }`}>
                      {log.eventType === 'SolarSystemPurchase' ? 'Closed Sale (Purchase)' : log.eventType === 'SiteAuditBooked' ? 'Site Audit Completed' : 'Quote Generated (Lead)'}
                    </span>
                  </td>

                  <td className="py-3.5 px-4">
                    <div className="font-bold text-white text-xs">{log.leadName}</div>
                    <div className="text-[11px] text-slate-400">{log.region}</div>
                  </td>

                  <td className="py-3.5 px-4 font-medium text-emerald-400">
                    {log.presetPackage}
                  </td>

                  <td className="py-3.5 px-4 font-mono font-bold text-white text-sm">
                    ₦{log.estimatedValueNgn.toLocaleString()}
                  </td>

                  <td className="py-3.5 px-4 text-right">
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-500/10 text-emerald-400 text-[10px] font-bold border border-emerald-500/20">
                      <CheckCircle2 className="w-3 h-3" /> Sync Completed
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Campaign Wizard Modal */}
      <CampaignWizard
        isOpen={isWizardOpen}
        onClose={() => setIsWizardOpen(false)}
        onCampaignCreated={fetchGrowthMetrics}
        preSelectedPreset={wizardPreset}
      />

      {/* Marketing API & OAuth Settings Modal */}
      <MarketingApiSettings
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
        onSettingsUpdated={fetchGrowthMetrics}
      />
    </div>
  );
};
